
import InputLabelField from '@/components/form-control/InputLabelField'
import { useAuthContext } from '@/features/auth/context'
import useUser from '@/features/auth/hooks/useUser'
import { useConfigUI } from '@/features/config/context'
import useAddTurfQuality from '@/features/config/hooks/turfQuality/useAddTurfQuality'
import { NotifyService } from '@/helpers'
import useTranslation from '@/hooks/useTranslation'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Container,
  Fade,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Controller, Resolver, useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import classes from './style.module.scss'
import useSearchTurfQualityId from '@/features/config/hooks/turfQuality/useSearchTurfQualityId'
import useEditTurfQuality from '@/features/config/hooks/turfQuality/useEditTurfQuality'

interface FormPipeProps {
  id?: number
  parentId?: number
}

export interface IFormTurfQuality {
  id?: number
  name: string
  interfaceId: number
  irrigationFactor: number
}

const initialValues = {
  id: 0,
  name: '',
  interfaceId: 0,
  irrigationFactor: undefined,
}

const FormTurfQualityView = ({ id }: FormPipeProps) => {
  const { closeSidebar } = useConfigUI()
  const { t } = useTranslation()
  const { interfaceId } = useAuthContext()
  const { data: user } = useUser()

  const schema = yup.object({
    name: yup.string().required('Turf Quality Name is required'),
    irrigationFactor: yup
      .number()
      .required('Irrigation Factor is required')
      .positive('Irrigation Factor must be a positive number')
      .min(0, 'Irrigation Factor cannot be negative')
      .max(200, 'Irrigation Factor cannot be >200'),
  })

  const { data, isFetching } = useSearchTurfQualityId(Number(id))

  const { mutate: addTurfQuality, isLoading: loadingAddTurfQuality } =
    useAddTurfQuality()
  const { mutate: updateTurfQuality, isLoading: loadingUpdateTurfQuality } =
    useEditTurfQuality()

  const form = useForm<IFormTurfQuality>({
    resolver: yupResolver(schema) as unknown as Resolver<IFormTurfQuality, any>,
  })

  const { reset, control, handleSubmit } = form

  const handleSave = (data: IFormTurfQuality) => {
    if(!interfaceId) return 
    if (id) {
      updateTurfQuality({ ...data, id })
    } else {
      addTurfQuality({ ...data, interfaceId })
    }
  }

  useEffect(() => {
    ;(async () => {
      if(!interfaceId) return 
      if (id && data) {
        reset({ ...data, id })
      } else {
        reset({ ...initialValues, interfaceId })
      }
    })()
  }, [data, id])

  return (
    <Container className={classes.container}>
      {isFetching && (
        <Stack
          width="377px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!isFetching && (
        <Fade in={!isFetching}>
          <form onSubmit={handleSubmit(handleSave)}>
            <Stack className={classes.formEdit}>
              <Stack className={classes.wrapper}>
                <Typography className={classes.editTitlePipe}>
                  {!id && t('addNewTurfQuality')}
                  {!!id && t('editTurfQuality')}
                </Typography>
                <Stack direction="row">
                  <Button
                    variant="secondary"
                    size="small"
                    className={classes.btnCancel}
                    onClick={() => closeSidebar()}
                  >
                    {t('commonActionsCancel')}
                  </Button>
                  <LoadingButton
                    type="submit"
                    size="small"
                    variant="primary"
                    className={classes.loadingBtn}
                    loading={
                      id ? loadingUpdateTurfQuality : loadingAddTurfQuality
                    }
                  >
                    {t('commonActionsSave')}
                  </LoadingButton>
                </Stack>
              </Stack>

              <Stack>
                <InputLabelField
                  isRequired
                  name="name"
                  form={form}
                  label={t('turfQualityName')}
                  placeholder={t('turfQualityName')}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    width: {},
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />

                <Controller
                  name="irrigationFactor"
                  control={control}
                  render={({ field: { value, ...field } }) => {
                    return (
                      <InputLabelField
                        {...field}
                        isRequired
                        label={t('irrigationFactor')}
                        value={value}
                        form={form}
                        placeholder="0.00"
                        style={{
                          height: 50,
                          borderRadius: '4px',
                          marginTop: '12px',
                          width: {},
                        }}
                        padding="8px 16px"
                      />
                    )
                  }}
                />
              </Stack>
            </Stack>
          </form>
        </Fade>
      )}
    </Container>
  )
}

export default FormTurfQualityView
